import { AudioManager } from "../../../AudioManager";
import { GameVars } from "../../../GameVars";
import { RayEffect } from "./RayEffect";
import { StripedOrbEffect } from "./StripedOrbEffect";
import { BoardContainer } from "../BoardContainer";
import { BoardManager } from "../BoardManager";
import { OrbsPool } from "./OrbsPool";

export class Orb extends Phaser.GameObjects.Container {

    public static readonly SIZE = 75;

    private static readonly SPEED = 5; // era 5
    private static readonly BOUNCING_SPEED = 1.5;

    public id: number;
    public orbData: Candy.OrbData;
    public p: {c: number, r: number};
    public setToImplode: boolean;
    public displacedByWrappedImplosion: boolean;

    private boardContainer: BoardContainer;
    private orbImg: Phaser.GameObjects.Image;
    private orbImgOn: Phaser.GameObjects.Image;
    private pulsatingScaleTween: Phaser.Tweens.Tween;
    private pulsatingAlphaTween: Phaser.Tweens.Tween;
    private collapsing: boolean;
    private f1: number;
    private framesIdle1: number;
    private framesIdle2: number;
    private f2: number;
    private framesToMutate: number;
    private setToMutate: boolean;
    private setToRemutate: boolean;
    private remutatedType: number;
    private framesToRemutate: number;
    private d: number;
    private moving: boolean;
    private orbFallingSpeed: number;
    private speed: number;
    private bouncing: boolean;
    private collapsingPosition: {x: number, y: number};
    private scalingUpHorizontalStriped: boolean;
    private scalingUpVerticalStriped: boolean;
    private pBomb: {c: number, r: number};
    private shine: Phaser.GameObjects.Sprite;
    private reshuffling: boolean;

    constructor(scene: Phaser.Scene, boardContainer: BoardContainer) {
        
        super(scene);

        this.boardContainer = boardContainer;
        this.collapsing = false;

        this.orbImg = new Phaser.GameObjects.Image(this.scene, 0, 0, "texture_atlas_1");
        this.add(this.orbImg);

        this.orbImgOn = new Phaser.GameObjects.Image(this.scene, 0, 0, "texture_atlas_1");
        this.orbImgOn.visible = false;
        this.add(this.orbImgOn);
    }

    public update(): void {

        if (this.reshuffling) {

            this.f1 ++;

            if (this.f1 >= this.framesIdle1)  {

                if (this.f1 >= this.framesIdle2) {

                    const dx = (this.orbData.prevPos.c * Orb.SIZE - this.x) * .125;
                    const dy = (this.orbData.prevPos.r * Orb.SIZE - this.y) * .125;

                    if (Math.abs(dx) + Math.abs(dy) < .05 ) {

                        this.reshuffling = false;
                        this.x = this.orbData.prevPos.c * Orb.SIZE;
                        this.y = this.orbData.prevPos.r * Orb.SIZE;

                        this.boardContainer.onReshuffledOrbReachedItsFinalPosition();

                    } else {

                        this.x += dx;
                        this.y += dy;
                    }

                } else {
                    this.x += (this.x - this.orbData.prevPos.c * Orb.SIZE) * .0035;
                    this.y += (this.y - this.orbData.prevPos.r * Orb.SIZE) * .0035;
                }
            }
           
            return;
        }

        if (this.displacedByWrappedImplosion) {

            const x = this.p.c * Orb.SIZE;
            const y = this.p.r * Orb.SIZE;

            const dx = (x - this.x) * .25 * GameVars.speedFactor;
            const dy = (y - this.y) * .25 * GameVars.speedFactor;

            this.x += dx;
            this.y += dy;

            if (Math.abs(dx) + Math.abs(dy) < .05) {

                this.displacedByWrappedImplosion = false;

                this.x = x;
                this.y = y;
            }
        }

        if (this.boardContainer.areOrbsDisplacedByWrappedImplosion) {
            return;
        }

        if (this.setToImplode) {

            if (this.pBomb && this.f1 === this.framesIdle1 - 15) {
                
                this.addBombRayEffect();
            }

            if (this.f1 === this.framesIdle1) {
                this.triggerImplosionAnimation();
            }

            this.f1 ++;
        }

        if (this.moving) {

            this.f1 ++;

            if (this.f1 >= this.framesIdle1) {

                const py = (this.p.r + this.d) * Orb.SIZE + Orb.SIZE * .15; // esto se puede precalcular

                this.speed += (this.orbFallingSpeed - this.speed) * .1;

                this.y += this.speed * GameVars.speedFactor;
    
                if (this.y >= py) {
                   
                    this.moving = false;
                    this.bouncing = true;
    
                    this.y = py;

                    AudioManager.playSound("candy_settles");
                }
            }
        } 

        if (this.bouncing) {

            const py = (this.p.r + this.d) * Orb.SIZE;

            this.y -= Orb.BOUNCING_SPEED * GameVars.speedFactor;

            if (this.y <= py) {

                this.bouncing = false;
                this.y = py;
                this.speed = 0;

                this.p.r += this.d;

                this.boardContainer.onOrbReachedItsPosition();
            }
        }

        if (this.setToMutate) {

            if (this.f2 === this.framesToMutate) {
                this.setToMutate = false;
                this.mutate();

                if (this.setToRemutate) {
                    this.setToRemutate = false;
                    this.mutateTo(this.remutatedType, this.framesToRemutate + 1);
                }
            }

            this.f2 ++;
        }

        if (this.collapsing) {

            const dx = (this.collapsingPosition.x - this.x) * .125;
            const dy = (this.collapsingPosition.y - this.y) * .125;

            this.x += dx;
            this.y += dy;

            if (Math.abs(dx) + Math.abs(dy) < 2) {
                this.boardContainer.onOrbImploded(this);
                this.removeOrbFromBoard();
            }
        }

        if (this.scalingUpHorizontalStriped) {

            let s = this.scale;
            s *= 1.05;

            this.setScale(s);

            if (s >= 1) {
   
                this.scalingUpHorizontalStriped = false;
                this.scalingUpVerticalStriped = true;
                this.f1 = 0;
                this.emitBigRay();
            }
        }

        if (this.scalingUpVerticalStriped) {

            this.f1 ++;

            if (this.f1 === 30) {
                this.setScale(.3);
                this.orbImg.visible = true;
                this.orbData.type = Candy.OrbType.STRIPPED_VER;
                this.orbImg.setFrame("orb-" + this.orbData.color + "-big-v");
            }

            if (this.f1 > 30) {

                let s = this.scale;
                s *= 1.05;
    
                this.setScale(s);

                if (s >= 1) {

                    this.setScale(1);
                    this.scalingUpVerticalStriped = false;
                    this.emitBigRay();

                    this.boardContainer.onOrbImploded(this);
                }
            }
        }

        if (Math.random() > .9995 && this.scene && !GameVars.gameOver && this.shine === null && this.orbData.type !== Candy.OrbType.BOMB && !this.setToImplode && !this.moving && !this.bouncing) {
        
            this.shine = new Phaser.GameObjects.Sprite(this.scene, 0, 0, "texture_atlas_1");
            this.scene.add.existing(this.shine);
            this.add(this.shine);

            this.shine.anims.play("shine");
            this.shine.on("animationcomplete", function(): void {
                if (this.shine) {
                    this.shine.destroy();
                    this.shine = null;
                }
            }, this);
        }
    }

    public reset(orbData: Candy.OrbData, p: {c: number, r: number}): void {

        this.orbData = orbData;
        this.p = p;
        this.x = this.p.c * Orb.SIZE;
        this.y = this.p.r * Orb.SIZE;
        this.moving = false;
        this.bouncing = false;
        this.d = null;
        this.f1 = 0;
        this.f2 = 0;
        this.framesIdle1 = 0;
        this.framesIdle2 = 0;
        this.displacedByWrappedImplosion = false;
        this.orbFallingSpeed = null;
        this.setToImplode = false;
        this.speed = 0;
        this.alpha = 1;
        this.collapsing = false;
        this.id = OrbsPool.id;
        this.pBomb = null;
        this.scalingUpHorizontalStriped = false;
        this.scalingUpVerticalStriped = false;
        this.collapsingPosition = null;
        this.shine = null;
        this.framesToMutate = 0;
        this.setToMutate = false;
        this.setToRemutate = false;
        this.setScale(1);
        this.orbImg.visible = true;
        this.orbImg.setScale(1);
        this.orbImgOn.visible = false;
        this.orbImgOn.setScale(1);
        this.reshuffling = false;

        if (this.orbData.type === Candy.OrbType.NORMAL) {
            this.orbImg.setFrame("orb-" + this.orbData.color);
        } else {

            if (this.orbData.type ===  Candy.OrbType.STRIPPED_VER) {
                this.orbImg.setFrame("orb-" + this.orbData.color + "-v");
            } else if (this.orbData.type === Candy.OrbType.STRIPPED_HOR) {
                this.orbImg.setFrame("orb-" + this.orbData.color + "-h");
            } else if (this.orbData.type === Candy.OrbType.WRAPPED) {
                this.orbImg.setFrame("orb-" + this.orbData.color + "-wrapped");
            } else if (this.orbData.type === Candy.OrbType.BOMB) {
                this.orbImg.setFrame("color-bomb");
            } else if (this.orbData.type === Candy.OrbType.PULSATING) {

                this.orbImg.setFrame("orb-" + this.orbData.color);

                this.pulsatingScaleTween = this.scene.tweens.add({
                    targets: [this.orbImg, this.orbImgOn],
                    scaleX: .9,
                    scaleY: .9,
                    ease: Phaser.Math.Easing.Linear,
                    duration: 250,
                    repeat: -1,
                    yoyo: true
                });

                this.pulsatingAlphaTween = this.scene.tweens.add({
                    targets: this.orbImgOn,
                    alpha: 0,
                    ease: Phaser.Math.Easing.Linear,
                    duration: 250,
                    repeat: -1,
                    yoyo: true
                });
            }
        }
    }

    public reshuffle(): void {

        this.reshuffling = true;

        this.framesIdle1 = Math.floor(Math.random() * 25);
        this.framesIdle2 = Math.floor(Math.random() * 20 + 30);
        this.f1 = 0;

        this.p = this.orbData.prevPos;
    }

    public move(d: number, framesIdle: number): void {

        this.d = d;
        this.framesIdle1 = framesIdle;
        this.moving = true;
        this.f1 = 0;
        this.orbFallingSpeed = Orb.SPEED * (1 + this.d * .75) * (1 + (this.p.r + d) * .2);
    }

    public select(): void {

        this.alpha = .5;
    }

    public unSelect(): void {

        this.alpha = 1;
    }

    public onMismatch(p: {c: number, r: number}): void {

        this.unSelect();

        const x = p.c * Orb.SIZE;
        const y = p.r * Orb.SIZE;

        this.scene.tweens.add({
            targets: this,
            x: x,
            y: y,
            ease: Phaser.Math.Easing.Cubic.Out,
            duration: 350,
            yoyo: true,
            onComplete: function(): void  {
                BoardManager.currentInstance.onMissMatchOrbReturnedToItsPosition();
            },
            onCompleteScope: this
        });
    }

    public onMatch(p: {c: number, r: number}, isSecondOrb: boolean): void {

        this.unSelect();

        this.p = p;

        const x = this.p.c * Orb.SIZE;
        const y = this.p.r * Orb.SIZE;

        this.scene.tweens.add({
            targets: this,
            x: x,
            y: y,
            ease: Phaser.Math.Easing.Cubic.Out,
            duration: 300,
            delay:  0,
            onComplete: function(): void {

                if (isSecondOrb) {
                   BoardManager.currentInstance.onMatchAnimationCompleted();
                }

            },
            onCompleteScope: this
        }); 

        if (isSecondOrb) {
            AudioManager.playSound("swipe_candies");
        }
    }

    public implode(eliminatedOrbData: Candy.EliminatedOrb): void {

        this.collapsing = eliminatedOrbData.collapsing;
        this.pBomb = eliminatedOrbData.pBomb;

        if (this.collapsing || eliminatedOrbData.isComboWrappedWrappedOrb) {

            if (eliminatedOrbData.isComboWrappedWrappedOrb) {
                this.boardContainer.orbsContainer.bringToTop(this);
                this.mutateTo(Candy.OrbType.BIG_STRIPED, eliminatedOrbData.n);
            } else {
                this.boardContainer.orbsContainer.sendToBack(this);
            }
           
            this.collapsingPosition = {x: eliminatedOrbData.collapsingPosition.c * Orb.SIZE, y: eliminatedOrbData.collapsingPosition.r * Orb.SIZE};

        } else {

            this.f1 = 0;
            this.framesIdle1 = eliminatedOrbData.n * 3 * Math.floor(1 / GameVars.speedFactor);
            this.setToImplode = true;

            this.boardContainer.orbsContainer.bringToTop(this);

            // PARA EL CASO DE UN COMO STRIPPED STRIPPED EN EL Q UNO CAMBIA DE VERTICAL A HORIZONTAL Y VICEVERSA
            if (this.orbData.type !== eliminatedOrbData.type && !this.setToMutate && (eliminatedOrbData.type === Candy.OrbType.STRIPPED_VER || eliminatedOrbData.type === Candy.OrbType.STRIPPED_HOR)) {
               
                this.orbData.type = eliminatedOrbData.type;

                if (this.orbData.type ===  Candy.OrbType.STRIPPED_VER) {
                    this.orbImg.setFrame("orb-" + this.orbData.color + "-v");
                } else if (this.orbData.type === Candy.OrbType.STRIPPED_HOR) {
                    this.orbImg.setFrame("orb-" + this.orbData.color + "-h");
                } 
            }
        }
    }

    public onWrappedOrbImploded(delta: {x: number, y: number}): void {

        this.displacedByWrappedImplosion = true;

        this.x += delta.x * Orb.SIZE;
        this.y += delta.y * Orb.SIZE;
    }

    public mutateTo(type: number, framesToMutate: number, pBomb?: {c: number, r: number}): void {

        this.pBomb = pBomb || null;

        if (this.setToMutate) {

            this.setToRemutate = true;
            this.remutatedType = type;
            this.framesToRemutate = framesToMutate;
            return;
        }

        this.orbData.type = type;
        this.f2 = 0;
        this.framesToMutate = framesToMutate;
        this.setToMutate = true;
    }

    public removeOrbFromBoard(): void {

        if (this.pulsatingScaleTween) {
            this.pulsatingScaleTween.remove();
            this.pulsatingScaleTween = null;
        }

        if (this.pulsatingAlphaTween) {
            this.pulsatingAlphaTween.remove();
            this.pulsatingAlphaTween = null;
        }

        this.boardContainer.removeOrb(this);
    }

    private mutate(): void  {

        if (this.orbImgOn.visible) {
            this.orbImgOn.visible = false;
        }

        this.boardContainer.orbsContainer.bringToTop(this);

        switch (this.orbData.type)  {
            
            case Candy.OrbType.STRIPPED_VER:
            case Candy.OrbType.STRIPPED_HOR:
                this.mutateToStripped();
                break;
            case Candy.OrbType.WRAPPED:
                this.mutateToWrapped();
                break;
            case Candy.OrbType.PULSATING:
                this.mutateToPulsating();
                break;
            case Candy.OrbType.BIG_STRIPED:
                this.mutateToBigStriped();
                break;
            case Candy.OrbType.BOMB:
                this.mutateToBomb();
                break;
            default:
        }

        if (this.pBomb !== null) {

            const rayEffect = new RayEffect(this.scene, this.pBomb, this.p);
            this.boardContainer.add(rayEffect);
        }
    }

    private mutateToStripped(): void  {

        let mutatedOrbImgTextureName: string;
        let linesImgTextureName: string;

        if (this.orbData.type === Candy.OrbType.STRIPPED_VER) {
            mutatedOrbImgTextureName = "orb-" + this.orbData.color + "-v";
            linesImgTextureName = "orb-" + this.orbData.color + "-v-up";
        } else {
            mutatedOrbImgTextureName = "orb-" + this.orbData.color + "-h";
            linesImgTextureName = "orb-" + this.orbData.color + "-h-right";
        } 
 
        const linesImg = new Phaser.GameObjects.Image(this.scene, 0, 0, "texture_atlas_1", linesImgTextureName);
        linesImg.blendMode = Phaser.BlendModes.SCREEN;
        linesImg.alpha = 0;
        if (this.orbData.type === Candy.OrbType.STRIPPED_VER) {
            linesImg.scaleY = 2;
        } else {
            linesImg.scaleX = 2;
        }
        this.add(linesImg);

        this.scene.tweens.add({
            targets: linesImg,
            alpha: 1,
            ease: Phaser.Math.Easing.Cubic.In,
            duration: 100,
            onComplete: function(): void {
                // para que uno q se esta transformando y va a ser eliminado se vea sobre
                // los posibles rayos de un stripped q explota
                this.boardContainer.orbsContainer.bringToTop(this);
            },
            onCompleteScope: this
        });

        this.scene.tweens.add({
            targets: linesImg,
            scaleX: 1,
            scaleY: 1,
            ease: Phaser.Math.Easing.Cubic.In,
            duration: 300,
            onComplete: function(): void {

                linesImg.destroy();

                this.orbImg.setFrame(mutatedOrbImgTextureName);
                this.orbImg.setScale(1.1);

                this.scene.tweens.add({
                    targets: this.orbImg,
                    scaleX: 1,
                    scaleY: 1,
                    ease: Phaser.Math.Easing.Cubic.Out,
                    duration: 600,
                    onComplete: function(): void {
                        if (this.setToImplodeAndMutate) {
                            this.removeOrbFromBoard();
                        }
                    },
                    onCompleteScope: this
                });
            },
            onCompleteScope: this
        });

        AudioManager.playSound("special_candy_created");
    }

    private mutateToWrapped(): void {
     
        const mutatedOrbImgTextureName = "orb-" + this.orbData.color + "-wrapped";

        this.orbImg.setFrame(mutatedOrbImgTextureName);

        const wrappedFX = new Phaser.GameObjects.Sprite(this.scene, 0, 0, "texture_atlas_7");
        this.scene.add.existing(wrappedFX);
        wrappedFX.anims.play("wrapped_transformation");
        wrappedFX.on("animationcomplete", function(): void {
            wrappedFX.destroy();
        }, this);
        this.add(wrappedFX);

        AudioManager.playSound("special_candy_created");
    }

    private emitBigRay(): void {

        this.orbImg.visible = false;

        const stripedOrbEffect = new StripedOrbEffect(this.scene, this, true);
        this.add(stripedOrbEffect);
    }

    private mutateToPulsating(): void {

        this.orbImg.setFrame("orb-" + this.orbData.color);

        this.orbImgOn.setFrame( "orb-" + this.orbData.color + "-on");
        this.orbImgOn.visible = true;

        this.pulsatingScaleTween = this.scene.tweens.add({
            targets: [this.orbImg, this.orbImgOn],
            scaleX: .9,
            scaleY: .9,
            ease: Phaser.Math.Easing.Linear,
            duration: 250,
            repeat: -1,
            yoyo: true
        });

        this.addWrappedPopFX();

        this.boardContainer.onWrappedOrbImploded(this.p);
    }

    private mutateToBigStriped(): void {

        this.scalingUpHorizontalStriped = true;
        this.scalingUpVerticalStriped = false;
        
        // primero q sea un striped horizontal
        this.orbImg.setFrame("orb-" + this.orbData.color + "-big-h");

        this.setScale(.3);
    }

    private mutateToBomb(): void {

        this.orbImg.visible = false;

        const bombTransformationEffect = new Phaser.GameObjects.Sprite(this.scene, 0, 0, "texture_atlas_1");
        this.scene.add.existing(bombTransformationEffect);
        bombTransformationEffect.anims.play("bomb_transformation");
        bombTransformationEffect.on("animationcomplete", function(): void {
            bombTransformationEffect.destroy();
            this.orbImg.visible = true;
            this.orbImg.setFrame("color-bomb");
        }, this);
        this.add(bombTransformationEffect);

        AudioManager.playSound("special_candy_created");
    }

    private addWrappedPopFX(): void {

        let tintColor: number;
        
        switch (this.orbData.color) {
            case 1:
                tintColor = 0x08D6FF;
                break;
            case 2:
                tintColor = 0x52EF1F;
                break;
            case 3:
                tintColor = 0xFF8502;
                break;
            case 4:
                tintColor = 0xF6C713;
                break;
            case 5:
                tintColor = 0xED3EFF;
                break;
            case 6:
                tintColor = 0xFD2958;
                break;
            case 7:
                tintColor = 0xED3EFF;
                break;
            default:
                break;
        }

        const wrappedPopFX = new Phaser.GameObjects.Sprite(this.scene, 0, 0, "texture_atlas_7");
        this.scene.add.existing(wrappedPopFX);
        wrappedPopFX.angle = Math.random() * 360;
        wrappedPopFX.setScale(1.1);
        wrappedPopFX.anims.play("simple_wrapped");
        wrappedPopFX.tint = tintColor;
        wrappedPopFX.blendMode = Phaser.BlendModes.SCREEN;
        this.add(wrappedPopFX);

        AudioManager.playSound("wrapped_explosion");
    } 

    private triggerImplosionAnimation(): void {

        this.boardContainer.onOrbImploded(this);

        this.orbImg.visible = false;

        if (this.orbImgOn) {
            this.orbImgOn.visible = false;
        }
    
        if (this.orbData.type === Candy.OrbType.STRIPPED_HOR || this.orbData.type === Candy.OrbType.STRIPPED_VER) {

            const stripedOrbEffect = new StripedOrbEffect(this.scene, this);
            this.add(stripedOrbEffect);

            this.boardContainer.orbsContainer.bringToTop(this);

        } else {

            if (this.orbData.type === Candy.OrbType.PULSATING) {

                this.boardContainer.onWrappedOrbImploded(this.p);
                this.addWrappedPopFX();
            }

            const popFX = new Phaser.GameObjects.Sprite(this.scene, 0, 0, "texture_atlas_7");
            this.scene.add.existing(popFX);

            if (this.orbData.type === Candy.OrbType.BOMB) {
                popFX.anims.play("bomb_implosion");
            } else {

                popFX.angle = Math.random() * 360;
                popFX.anims.play("popped");

                AudioManager.playSound("match_simple", false, 1, Math.random() * 3 + 1);
            }
           
            popFX.on("animationcomplete", this.removeOrbFromBoard, this);
            this.add(popFX);
        }
    }

    private addBombRayEffect(): void {

        const rayEffect = new RayEffect(this.scene, this.pBomb, this.p);
        this.boardContainer.add(rayEffect);
    }
}
